import { keyframes } from '@emotion/react';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TimerIcon from '@mui/icons-material/Timer';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, GridToolbar } from '@mui/x-data-grid';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loading } from '@/core/components';
import { functions, storage } from '@/core/services/firebase';
import {
  createRace,
  getClients,
  getRaces,
  updateRace,
  updateRaceNftInfo,
  updateRaceTimingInfo,
} from '@/core/services/operator';
import type { Client, Race, TimingLocation } from '@/core/types';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/** 運営管理画面用グラデーション（黄→赤） */
const operatorGradient = 'linear-gradient(to right, #ffff00, #ff0000)';

/** Timestamp を date 用の文字列に変換 */
function formatDate(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type ModalType = 'basic' | 'contract' | 'linkContract' | 'nft' | 'timing' | null;

export function RacesPage() {
  const [races, setRaces] = useState<Race[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsMap, setClientsMap] = useState<Record<string, Client>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // アクションメニュー
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRace, setSelectedRace] = useState<Race | null>(null);

  // モーダル状態
  const [modalType, setModalType] = useState<ModalType>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // 基本情報フォーム
  const [formClientId, setFormClientId] = useState('');
  const [formRaceName, setFormRaceName] = useState('');
  const [formAlias, setFormAlias] = useState('');
  const [formRaceDate, setFormRaceDate] = useState('');
  const [formStartAt, setFormStartAt] = useState('');
  const [formEndAt, setFormEndAt] = useState('');

  // コントラクトフォーム
  const [formChain, setFormChain] = useState<'soneium'>('soneium');
  const [formContractName, setFormContractName] = useState('');
  const [formContractSymbol, setFormContractSymbol] = useState('');
  const [formContractDescription, setFormContractDescription] = useState('');
  const [formLogoFile, setFormLogoFile] = useState<File | null>(null);
  const [formLogoPreview, setFormLogoPreview] = useState<string | null>(null);
  const [formContractAddress, setFormContractAddress] = useState('');

  // NFT設定フォーム
  const [formTokenName, setFormTokenName] = useState('');
  const [formTokenDescription, setFormTokenDescription] = useState('');

  // 計測連携フォーム
  const [formTimingEventId, setFormTimingEventId] = useState('');
  const [formLocations, setFormLocations] = useState<TimingLocation[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [racesData, clientsData] = await Promise.all([getRaces(), getClients()]);
      setRaces(racesData);
      setClients(clientsData);
      const map: Record<string, Client> = {};
      for (const client of clientsData) {
        map[client.id] = client;
      }
      setClientsMap(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // メニューを開く
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, race: Race) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRace(race);
  }, []);

  // メニューを閉じる
  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // モーダルを開く
  const openModal = (type: ModalType, race?: Race) => {
    setFormError(null);
    setFormSuccess(null);
    handleMenuClose();

    if (type === 'basic') {
      if (race) {
        // 編集モード
        setFormClientId(race.clientId);
        setFormRaceName(race.raceName);
        setFormAlias(race.alias);
        setFormRaceDate(race.raceDate || '');
        setFormStartAt(formatDate(race.startAt?.toDate() || null));
        setFormEndAt(formatDate(race.endAt?.toDate() || null));
      } else {
        // 作成モード
        setFormClientId('');
        setFormRaceName('');
        setFormAlias('');
        setFormRaceDate('');
        setFormStartAt('');
        setFormEndAt('');
      }
    } else if (type === 'contract' && race) {
      setFormChain('soneium');
      setFormContractName('');
      setFormContractSymbol('');
      setFormContractDescription('');
      setFormLogoFile(null);
      setFormLogoPreview(null);
    } else if (type === 'linkContract' && race) {
      setFormChain('soneium');
      setFormContractAddress('');
      setFormContractName('');
      setFormContractSymbol('');
      setFormContractDescription('');
    } else if (type === 'nft' && race) {
      setFormTokenName(race.tokenName || race.contractName || '');
      setFormTokenDescription(race.tokenDescription || '');
    } else if (type === 'timing' && race) {
      setFormTimingEventId(race.timingEventId || '');
      setFormLocations(race.locations || []);
    }

    setModalType(type);
  };

  // モーダルを閉じる
  const closeModal = () => {
    setModalType(null);
    setSelectedRace(null);
    setFormError(null);
    setFormSuccess(null);
  };

  // 基本情報の保存
  const handleBasicSubmit = async () => {
    if (!formClientId) {
      setFormError('主催者を選択してください');
      return;
    }
    if (!formRaceName.trim()) {
      setFormError('大会名を入力してください');
      return;
    }
    if (!formAlias.trim()) {
      setFormError('エイリアスを入力してください');
      return;
    }
    if (!formStartAt || !formEndAt) {
      setFormError('開始日と終了日を入力してください');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (selectedRace) {
        // 更新
        await updateRace(selectedRace.id, {
          clientId: formClientId,
          raceName: formRaceName.trim(),
          raceDate: formRaceDate,
          startAt: new Date(`${formStartAt}T00:00:00`),
          endAt: new Date(`${formEndAt}T23:59:59`),
        });
      } else {
        // 作成
        await createRace({
          clientId: formClientId,
          raceName: formRaceName.trim(),
          alias: formAlias.trim(),
          startAt: new Date(`${formStartAt}T00:00:00`),
          endAt: new Date(`${formEndAt}T23:59:59`),
        });
      }
      closeModal();
      fetchData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // コントラクトデプロイ
  const handleContractDeploy = async () => {
    if (!selectedRace) return;

    if (!formContractName.trim()) {
      setFormError('コントラクト名を入力してください');
      return;
    }
    if (!formContractSymbol.trim()) {
      setFormError('シンボルを入力してください');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      // ロゴをアップロード
      let logoFileName = '';
      if (formLogoFile) {
        const ext = formLogoFile.name.slice(formLogoFile.name.lastIndexOf('.'));
        logoFileName = `${Math.random().toString(36).slice(-8)}${ext}`;
        const logoRef = ref(storage, `contract/${logoFileName}`);
        await uploadBytes(logoRef, formLogoFile);
        await getDownloadURL(logoRef);
      }

      // Function呼び出し
      const deployContract = httpsCallable(functions, 'deployContractToRace');
      await deployContract({
        raceId: selectedRace.id,
        contractName: formContractName.trim(),
        contractSymbol: formContractSymbol.trim(),
        contractDescription: formContractDescription.trim(),
        chain: formChain,
        logoFileName,
      });

      setFormSuccess('コントラクトのデプロイを開始しました');
      setTimeout(() => {
        closeModal();
        fetchData();
      }, 1500);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'デプロイに失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // コントラクト紐付け
  const handleContractLink = async () => {
    if (!selectedRace) return;

    if (!formContractAddress.trim()) {
      setFormError('コントラクトアドレスを入力してください');
      return;
    }
    if (!/^0x[a-fA-F0-9]{40}$/.test(formContractAddress.trim())) {
      setFormError('無効なコントラクトアドレスです');
      return;
    }
    if (!formContractName.trim()) {
      setFormError('コントラクト名を入力してください');
      return;
    }
    if (!formContractSymbol.trim()) {
      setFormError('シンボルを入力してください');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    // 投げっぱなし（ブラウザ側タイムアウト対策）だが、初期エラーはキャッチする
    const linkContract = httpsCallable(functions, 'linkContractToRace');
    linkContract({
      raceId: selectedRace.id,
      contractAddress: formContractAddress.trim(),
      contractName: formContractName.trim(),
      contractSymbol: formContractSymbol.trim(),
      contractDescription: formContractDescription.trim(),
      chain: formChain,
    }).catch((err) => {
      console.error('linkContractToRace error:', err);
      setFormSuccess(null);
      setFormError(`紐付け処理でエラーが発生しました: ${err instanceof Error ? err.message : 'Unknown error'}`);
    });

    setFormSuccess('紐付け処理を開始しました。このモーダルは閉じてOKです。');
    setSubmitting(false);
  };

  // NFT設定の保存
  const handleNftSubmit = async () => {
    if (!selectedRace) return;

    setSubmitting(true);
    setFormError(null);

    try {
      await updateRaceNftInfo(selectedRace.id, {
        tokenName: formTokenName.trim() || null,
        tokenDescription: formTokenDescription.trim() || null,
      });
      closeModal();
      fetchData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'NFT情報の更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // 計測地点の追加
  const handleAddLocation = () => {
    const newOrder = formLocations.length > 0 ? Math.max(...formLocations.map((l) => l.order)) + 1 : 0;
    setFormLocations([...formLocations, { locationId: '', name: '', order: newOrder }]);
  };

  // 計測地点の更新
  const handleUpdateLocation = (index: number, field: keyof TimingLocation, value: string | number) => {
    const newLocations = [...formLocations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    setFormLocations(newLocations);
  };

  // 計測地点の削除
  const handleRemoveLocation = (index: number) => {
    setFormLocations(formLocations.filter((_, i) => i !== index));
  };

  // 計測連携の保存
  const handleTimingSubmit = async () => {
    if (!selectedRace) return;

    for (const loc of formLocations) {
      if (!loc.locationId.trim() || !loc.name.trim()) {
        setFormError('計測地点のIDと名前を入力してください');
        return;
      }
    }

    setSubmitting(true);
    setFormError(null);

    try {
      await updateRaceTimingInfo(selectedRace.id, {
        timingEventId: formTimingEventId.trim() || null,
        locations: formLocations.map((l, i) => ({
          locationId: l.locationId.trim(),
          name: l.name.trim(),
          order: l.order ?? i,
        })),
      });
      closeModal();
      fetchData();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '計測連携情報の更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // ロゴファイル選択
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormLogoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setFormLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row as Race)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
      {
        field: 'clientName',
        headerName: '主催者',
        width: 150,
        valueGetter: (_value, row) => clientsMap[row.clientId]?.name || '-',
      },
      {
        field: 'raceName',
        headerName: '大会名',
        width: 200,
      },
      {
        field: 'alias',
        headerName: 'エイリアス',
        width: 120,
      },
      {
        field: 'contractStatus',
        headerName: 'コントラクト',
        width: 150,
        renderCell: (params) => {
          const race = params.row as Race;
          if (race.deployStatus === 'deploying') {
            return <Chip label="デプロイ中" color="info" size="small" />;
          }
          if (race.deployStatus === 'failed') {
            return <Chip label="失敗" color="error" size="small" />;
          }
          if (race.contractAddress) {
            const addr = race.contractAddress;
            return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
          }
          return '-';
        },
      },
      {
        field: 'startAt',
        headerName: '開始日',
        width: 120,
        valueGetter: (_value, row) => row.startAt?.toDate?.()?.toLocaleDateString() || '-',
      },
      {
        field: 'endAt',
        headerName: '終了日',
        width: 120,
        valueGetter: (_value, row) => row.endAt?.toDate?.()?.toLocaleDateString() || '-',
      },
    ],
    [clientsMap, handleMenuOpen]
  );

  if (loading) {
    return <Loading />;
  }

  const isEditing = !!selectedRace;
  const hasContract = selectedRace?.contractAddress;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {error}
        </Alert>
      )}

      {/* 統計カード */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              minWidth: 100,
              px: 2,
              py: 1,
              borderRadius: '8px',
              backgroundColor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              大会数
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {races.length}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchData}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: '#1D1B20',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}`,
                },
              }}
            >
              更新
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openModal('basic')}
              sx={{
                background: operatorGradient,
                color: '#1D1B20',
                borderRadius: '16px',
                '&:hover': {
                  background: operatorGradient,
                  filter: 'brightness(0.95)',
                },
              }}
            >
              新規作成
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* データグリッド */}
      <Paper
        sx={{
          height: 600,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <DataGrid
          rows={races}
          columns={columns}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ toolbar: GridToolbar }}
          slotProps={{ toolbar: { showQuickFilter: true } }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* アクションメニュー */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={() => selectedRace && openModal('basic', selectedRace)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>基本情報を編集</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedRace && openModal('contract', selectedRace)} disabled={!!hasContract}>
          <ListItemIcon>
            <RocketLaunchIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>コントラクトをデプロイ</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedRace && openModal('linkContract', selectedRace)} disabled={!!hasContract}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>コントラクトを紐付け</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedRace && openModal('nft', selectedRace)} disabled={!hasContract}>
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>NFT設定</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedRace && openModal('timing', selectedRace)}>
          <ListItemIcon>
            <TimerIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>計測連携</ListItemText>
        </MenuItem>
      </Menu>

      {/* 基本情報モーダル */}
      <Dialog open={modalType === 'basic'} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{isEditing ? '大会編集' : '大会作成'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>主催者</InputLabel>
              <Select value={formClientId} label="主催者" onChange={(e) => setFormClientId(e.target.value)}>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="大会名"
              value={formRaceName}
              onChange={(e) => setFormRaceName(e.target.value)}
              fullWidth
              required
              placeholder="例: 愛媛マラソン2026"
            />
            <TextField
              label="エイリアス（URL用）"
              value={formAlias}
              onChange={(e) => setFormAlias(e.target.value)}
              fullWidth
              required
              disabled={isEditing}
              helperText={isEditing ? 'エイリアスは変更できません' : '例: ehime2026'}
            />
            <TextField
              label="開催日"
              type="date"
              value={formRaceDate}
              onChange={(e) => setFormRaceDate(e.target.value)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: '9999-12-31' } }}
              helperText="NFTメタデータに表示される日付"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="開始日"
                type="date"
                value={formStartAt}
                onChange={(e) => setFormStartAt(e.target.value)}
                fullWidth
                required
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: '9999-12-31' } }}
              />
              <TextField
                label="終了日"
                type="date"
                value={formEndAt}
                onChange={(e) => setFormEndAt(e.target.value)}
                fullWidth
                required
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: '9999-12-31' } }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
              '&:hover': { backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${operatorGradient}` },
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleBasicSubmit}
            disabled={submitting}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : isEditing ? '更新' : '作成'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* コントラクトデプロイモーダル */}
      <Dialog open={modalType === 'contract'} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>コントラクトをデプロイ</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {formSuccess}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField label="エイリアス" value={selectedRace?.alias || ''} fullWidth disabled />
            <FormControl fullWidth required>
              <InputLabel>チェーン</InputLabel>
              <Select value={formChain} label="チェーン" onChange={(e) => setFormChain(e.target.value as 'soneium')}>
                <MenuItem value="soneium">Soneium</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="コントラクト名"
              value={formContractName}
              onChange={(e) => setFormContractName(e.target.value)}
              fullWidth
              required
              placeholder="例: Ehime Marathon 2026"
            />
            <TextField
              label="シンボル"
              value={formContractSymbol}
              onChange={(e) => setFormContractSymbol(e.target.value)}
              fullWidth
              required
              placeholder="例: EHIME2026"
            />
            <TextField
              label="コントラクト説明"
              value={formContractDescription}
              onChange={(e) => setFormContractDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="コントラクトの説明（OpenSea等で表示）"
            />
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderRadius: '16px',
                  border: '2px solid transparent',
                  backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                  color: '#1D1B20',
                }}
              >
                ロゴ画像をアップロード
                <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
              </Button>
              {formLogoPreview && (
                <Box sx={{ mt: 2 }}>
                  <img
                    src={formLogoPreview}
                    alt="Logo preview"
                    style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleContractDeploy}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <RocketLaunchIcon />}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {submitting ? 'デプロイ中...' : 'デプロイ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* コントラクト紐付けモーダル */}
      <Dialog open={modalType === 'linkContract'} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>コントラクトを紐付け</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {formSuccess}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField label="エイリアス" value={selectedRace?.alias || ''} fullWidth disabled />
            <FormControl fullWidth required>
              <InputLabel>チェーン</InputLabel>
              <Select value={formChain} label="チェーン" onChange={(e) => setFormChain(e.target.value as 'soneium')}>
                <MenuItem value="soneium">Soneium</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="コントラクトアドレス"
              value={formContractAddress}
              onChange={(e) => setFormContractAddress(e.target.value)}
              fullWidth
              required
              placeholder="0x..."
              helperText="Thirdwebで手動デプロイしたコントラクトアドレス"
            />
            <TextField
              label="コントラクト名"
              value={formContractName}
              onChange={(e) => setFormContractName(e.target.value)}
              fullWidth
              required
              placeholder="例: Ehime Marathon 2026"
            />
            <TextField
              label="シンボル"
              value={formContractSymbol}
              onChange={(e) => setFormContractSymbol(e.target.value)}
              fullWidth
              required
              placeholder="例: EHIME2026"
            />
            <TextField
              label="コントラクト説明"
              value={formContractDescription}
              onChange={(e) => setFormContractDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="コントラクトの説明（OpenSea等で表示）"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleContractLink}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <LinkIcon />}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {submitting ? '紐付け中...' : '紐付け'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* NFT設定モーダル */}
      <Dialog open={modalType === 'nft'} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>NFT設定</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="NFT名"
              value={formTokenName}
              onChange={(e) => setFormTokenName(e.target.value)}
              fullWidth
              placeholder="コントラクト名と同じでOK"
              helperText="NFTメタデータの name として使用"
            />
            <TextField
              label="NFT説明"
              value={formTokenDescription}
              onChange={(e) => setFormTokenDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="NFT個別の説明"
              helperText="NFTメタデータの description として使用"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleNftSubmit}
            disabled={submitting}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 計測連携モーダル */}
      <Dialog open={modalType === 'timing'} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>計測連携</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="計測イベントID"
              value={formTimingEventId}
              onChange={(e) => setFormTimingEventId(e.target.value)}
              fullWidth
              placeholder="例: 360634"
              helperText="タイム計測会社から発行される大会識別子"
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  計測地点
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={handleAddLocation}>
                  追加
                </Button>
              </Box>
              {formLocations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  計測地点が設定されていません
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {formLocations.map((loc, index) => (
                    <Box key={loc.locationId || `loc-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <TextField
                        label="地点ID"
                        value={loc.locationId}
                        onChange={(e) => handleUpdateLocation(index, 'locationId', e.target.value)}
                        size="small"
                        sx={{ width: 100 }}
                        placeholder="1"
                      />
                      <TextField
                        label="名前"
                        value={loc.name}
                        onChange={(e) => handleUpdateLocation(index, 'name', e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                        placeholder="スタート"
                      />
                      <TextField
                        label="順番"
                        type="number"
                        value={loc.order}
                        onChange={(e) => handleUpdateLocation(index, 'order', parseInt(e.target.value, 10) || 0)}
                        size="small"
                        sx={{ width: 80 }}
                      />
                      <IconButton size="small" onClick={() => handleRemoveLocation(index)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              borderRadius: '16px',
              border: '2px solid transparent',
              backgroundImage: `linear-gradient(#fff, #fff), ${operatorGradient}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              color: '#1D1B20',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleTimingSubmit}
            disabled={submitting}
            sx={{
              background: operatorGradient,
              color: '#1D1B20',
              borderRadius: '16px',
              '&:hover': { background: operatorGradient, filter: 'brightness(0.95)' },
              '&:disabled': { background: operatorGradient, opacity: 0.6 },
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
